import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { SyncProcessService } from './sync-process.service';
import {
  AppkitDropdownItem,
  handleAppkitValueSelect,
  mapToAppkitDropdownItems,
  syncAppkitDropdownSelection
} from '../../../shared/utils/appkit-dropdown.util';
import { setFormDropdownAriaLabel } from '../../shared/ftc-form-dropdown.support';

@Component({
    selector: 'gtw-sync-process',
    templateUrl: './sync-process.component.html',
    styleUrls: ['./sync-process.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SyncProcessComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  private activeRequests = 0;
  get isLoading(): boolean {
    return this.activeRequests > 0;
  }

  GENERAL_CONFIG: any = {};
  private headerData: any = {};
  private globalParams: any = {};
  private userSettings: any = {};
  filterParams: any = {};

  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';

  filteredTaxYearData: any[] = [];
  selectedTaxYear: string = '';
  scenarioDropDown: any[] = [];
  selectedScenario: string = '';
  showscenario = false;
  examScenario = false;
  dropdownLists: { [k: string]: any[] } = { parentME: [], filerLE: [] };

  parentDetails: any[] = [];
  parentME: any[] = [];
  filingGroupDropDown: any[] = [];
  filingGroup: string = '';
  filingEnityDetails: any[] = [];
  filerEntity: any[] = [];

  filingGroupDropWoPship: any[] = [];
  filingGroupSection: string = '';
  filingEnityDetailsForSectionDetails: any[] = [];
  filerEntitySection: string = '';

  filteredTaxYearDataView: AppkitDropdownItem[] = [];
  selectedTaxYearItem: AppkitDropdownItem | null = null;
  scenarioDropDownView: AppkitDropdownItem[] = [];
  selectedScenarioItem: AppkitDropdownItem | null = null;
  filingGroupDropWoPshipView: AppkitDropdownItem[] = [];
  filingGroupSectionItem: AppkitDropdownItem | null = null;
  filingEnityDetailsForSectionDetailsView: AppkitDropdownItem[] = [];
  filerEntitySectionItem: AppkitDropdownItem | null = null;
  filingGroupDropDownView: AppkitDropdownItem[] = [];
  filingGroupItem: AppkitDropdownItem | null = null;

  setFormDropdownAriaLabel = setFormDropdownAriaLabel;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private syncProcessService: SyncProcessService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private beginRequest(): void {
    this.activeRequests++;
    this.cdr.detectChanges();
  }

  private endRequest(): void {
    if (this.activeRequests > 0) this.activeRequests--;
    this.cdr.detectChanges();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.GENERAL_CONFIG = this.headerData.generalConfigData || {};
    this.globalParams = this.headerData.globalParams || {};
    this.userSettings = this.headerData.userSettingsData || {};
    const activeScreen =
      this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    // Fall back to the screen filter context: on these FTC system-settings screens
    // globalParams.tax_year can be empty, which would make the Tax Year dropdown
    // filter (desc <= currentYear && desc >= currentYear - 5) reject every year.
    this.curentyear = this.globalParams.tax_year || this.filterParams.tax_year || '';
    this.scenario = this.globalParams.scenario || this.filterParams.scenario || '';
    this.jcd_key = this.globalParams.jcd_key || this.filterParams.jcd_key || '';
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.checkLockStatus();
    this.getTaxYearDropDown();
    this.getParentMeDetails();
    this.cdr.detectChanges();
  }

  private checkLockStatus(): void {
    const lockparams = {
      action_code: 'n10cpv',
      scenario: this.scenario,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        lockparams,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        const locked = data?.jsonObject;
        if (locked?.[0]?.LOCK_STATUS == 'Y') {
          this.gtwSnackbarService.openSnackBar(
            'Tax Return has already been filed for tax year ' +
              this.filterParams.tax_year +
              '.',
            'gtw-snackbar--red',
            true,
            4
          );
        }
        if (locked?.[0]?.SCENARIO_DESC == 'EXAM') {
          this.examScenario = true;
        }
        this.cdr.detectChanges();
      });
  }

  getTaxYearDropDown(): void {
    const params = { action_code: 'e83e1x' };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        const taxYearData = _.filter(data?.jsonObject, (p: any) =>
          _.includes(['TAX_YEAR'], p.CODE_GROUP)
        );
        this.filteredTaxYearData = [];
        let currentYear = parseInt(this.curentyear, 10);
        if (isNaN(currentYear)) {
          // Last-resort fallback so the dropdown is never silently empty: anchor the
          // 5-year window on the newest tax year returned by the service.
          currentYear = taxYearData.reduce(
            (max: number, ty: any) => Math.max(max, parseInt(ty.CODE_DISP_DESC, 10) || 0),
            0
          );
        }
        for (const ty of taxYearData) {
          const desc = parseInt(ty.CODE_DISP_DESC, 10);
          if (
            ty.CODE_GROUP == 'TAX_YEAR' &&
            desc <= currentYear &&
            desc >= currentYear - 5
          ) {
            this.filteredTaxYearData.push(ty);
          }
        }
        this.filteredTaxYearData.sort(
          (a: any, b: any) =>
            parseInt(b.CODE_DISP_DESC, 10) - parseInt(a.CODE_DISP_DESC, 10)
        );
        this.refreshTaxYearDropdown();
        this.cdr.detectChanges();
      });
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year to get the Scenario.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    const params = {
      action_code: '7q0wiy',
      tax_year: this.selectedTaxYear,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.showscenario = true;
        this.refreshScenarioDropdown();
        this.cdr.detectChanges();
      });
  }

  onScenarioChange(): void {
    this.getCOAData();
    this.getFilingGroup();
    this.getFilingGroupDropDownWoPship();
  }

  private getCOAData(): void {
    const params = {
      action_code: 'md63zh',
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.cdr.detectChanges();
      });
  }

  getFilingGroup(): void {
    const params = {
      action_code: 'z60z07',
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.filingGroupDropDown = data?.jsonObject || [];
        this.refreshFilingGroupDropdown();
        this.cdr.detectChanges();
      });
  }

  getFilingEnityDetails(): void {
    const params: any = {
      action_code: '35gc5l',
      filing_group_key: this.filingGroup,
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
      parent_me_keys: this.parentME
        .map((m: any) => m.PARENT_ME_KEY)
        .join(),
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe(
        (data: any) => {
          this.filerEntity = [];
          this.filingEnityDetails = data?.jsonObject || [];
          this.syncDropdownList('filerLE', this.filingEnityDetails);
          this.cdr.detectChanges();
        },
        () => {
          this.filerEntity = [];
          this.filingEnityDetails = [];
          this.syncDropdownList('filerLE', this.filingEnityDetails);
          this.cdr.detectChanges();
        }
      );
  }

  getParentMeDetails(): void {
    const params = { action_code: 'hmkia6' };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.parentDetails = _.filter(
          data?.jsonObject,
          (d: any) => d.LEVEL_GROUP === 'Parent ME'
        );
        this.cdr.detectChanges();
      });
  }

  getFilingGroupDropDownWoPship(): void {
    const params = {
      action_code: '0udsuc',
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.filingGroupDropWoPship = data?.jsonObject || [];
        this.refreshFilingGroupSectionDropdown();
        this.cdr.detectChanges();
      });
  }

  getFilingEnityDetailsForSection(): void {
    const params = {
      action_code: '35gc5l',
      filing_group_key: this.filingGroupSection,
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
    };
    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        this.filerEntitySection = '';
        this.filingEnityDetailsForSectionDetails = data?.jsonObject || [];
        this.refreshFilerEntitySectionDropdown();
        this.cdr.detectChanges();
      });
  }

  private validateTaxYearAndScenario(): boolean {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Tax Year.',
        'gtw-snackbar--red',
        true,
        5
      );
      return false;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Scenario.',
        'gtw-snackbar--red',
        true,
        5
      );
      return false;
    }
    return true;
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  private runSyncWithLockCheck(
    confirmMessage: string,
    confirmHeader: string,
    actionCode: string,
    successMessage: string,
    errorMessage: string,
    extraParams: any = {},
    useSaveJSON = true
  ): void {
    if (!this.validateTaxYearAndScenario()) return;
    this.confirmAction(confirmHeader, confirmMessage, () => {
      this.executeSyncAfterConfirm(actionCode, successMessage, errorMessage, extraParams, useSaveJSON);
    });
  }

  private executeSyncAfterConfirm(
    actionCode: string,
    successMessage: string,
    errorMessage: string,
    extraParams: any = {},
    useSaveJSON = true
  ): void {
    const lockparams: any = {
      action_code: 'n10cpv',
      tax_year: this.selectedTaxYear,
      p_consol_group_key: extraParams.p_consol_group_key || '1',
      p_jcd_key: this.jcd_key,
      p_scenario: this.scenario,
    };
    if (extraParams.FILER_LE_KEY) {
      lockparams.FILER_LE_KEY = extraParams.FILER_LE_KEY;
    }

    this.beginRequest();
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        lockparams,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe((data: any) => {
        const locked = data?.jsonObject;
        const lockStatus = locked?.[0]?.LOCK_STATUS;
        const scenarioDesc = locked?.[0]?.SCENARIO_DESC;

        if (lockStatus == 'N' && scenarioDesc != 'EXAM') {
          const issueParams = this.gtwGlobal.isIssueIDEnabled(
            this.selectedTaxYear,
            this.selectedScenario
          );
          const params: any = {
            sso_id: this.userSettings?.user?.sso_id,
            action_code: actionCode,
            tax_year: this.selectedTaxYear,
            scenario: this.selectedScenario,
            jcd_key: this.jcd_key,
            issue_id: this.globalParams.issue_id,
            issue_short_desc: this.globalParams.issue_short_desc,
            scenarioCode: issueParams.scenarioCode,
            is_issue_key_enabled: issueParams.is_issue_key_enabled,
            ...extraParams,
          };
          delete params.p_consol_group_key;

          if (useSaveJSON) {
            this.beginRequest();
            this.jsonObjectFactory
              .saveJSON(
                this.GENERAL_CONFIG.base_url + '/saveJsonObject',
                params,
                this.headerData
              ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
              .subscribe(
                (result: any) => {
                  if (result?.callSuccess != 1 && result?.errorMessage) {
                    this.gtwSnackbarService.openSnackBar(
                      result.errorMessage,
                      'gtw-snackbar--red',
                      true,
                      4
                    );
                  } else {
                    this.gtwSnackbarService.openSnackBar(
                      successMessage,
                      'gtw-snackbar--green',
                      true,
                      4
                    );
                  }
                  this.cdr.detectChanges();
                },
                () => {
                  this.gtwSnackbarService.openSnackBar(
                    'Error occurred. Please try again.',
                    'gtw-snackbar--red',
                    true,
                    4
                  );
                  this.cdr.detectChanges();
                }
              );
          } else {
            this.gtwSnackbarService.openSnackBar(
              successMessage,
              'gtw-snackbar--green',
              true,
              4
            );
          }
        } else {
          this.gtwSnackbarService.openSnackBar(
            errorMessage,
            'gtw-snackbar--red',
            true,
            4
          );
        }
        this.cdr.detectChanges();
      });
  }

  syncExemptAssetsConsol(): void {
    this.runSyncWithLockCheck(
      'Are you sure to initiate the Sync process for Exempt Assets - FDII?',
      'Sync process for Exempt Assets - FDII',
      'oreknl',
      'Sync Process for Exempt Assets - FDII is initiated. Email will be sent, after the process is complete.',
      'Cannot proceed with Sync Exempt Assets - FDII Process for selected Tax Year and Scenario.'
    );
  }

  syncIntExpConsol(): void {
    this.runSyncWithLockCheck(
      'Are you sure to initiate the Sync process for Interest Expense Consol?',
      'Sync process for Interest Expense Consol',
      '2rt8bp',
      'Sync Process for Interest Expense (Consolidated) is initiated. Email will be sent, after the process is complete.',
      'Cannot proceed with Sync Interest Expense Process for selected Tax Year and Scenario.'
    );
  }

  syncRnDConsol(): void {
    this.runSyncWithLockCheck(
      'Are you sure to initiate the Sync process for R&D Consol?',
      'Sync process for R&D Consol',
      '5ddaxy',
      'Sync Process for Research & Development (Consolidated) is initiated. Email will be sent, after the process is complete.',
      'Cannot proceed with Sync Research & Development Process for selected Tax Year and Scenario.'
    );
  }

  syncFirstTierCFC(): void {
    this.runSyncWithLockCheck(
      'Are you sure to create the Adjustments for Investment First Tier CFC 861 Sourcing?',
      'Sync process for Investment First Tier CFC 861 Sourcing Adjustments',
      '9aeyyr',
      'Sync process for Investment First Tier CFC 861 Sourcing Adjustments is completed.',
      'Cannot proceed with Sync process for Investment First Tier CFC 861 Sourcing Adjustments for selected Tax Year and Scenario.',
      { p_adj_type: 'INV' }
    );
  }

  syncEPBumpSourcing(): void {
    this.runSyncWithLockCheck(
      'Are you sure to create the Adjustments for E&P Bump 861 Sourcing?',
      'Sync process for E&P Bump 861 Sourcing Adjustments',
      '9aeyyr',
      'Sync process for E&P Bump 861 Sourcing Adjustments is completed.',
      'Cannot proceed with Sync process for E&P Bump 861 Sourcing Adjustments for selected Tax Year and Scenario.',
      { p_adj_type: 'EP' }
    );
  }

  syncSec250Process(): void {
    if (!this.validateSec250()) return;
    this.runSyncWithLockCheck(
      'Are you sure to initiate the Sync process for Calculate Section 250?',
      'Sync process for Calculate Section 250',
      '85xrj3',
      'Sync Process for Calculate Section 250 is initiated. Email will be sent, after the process is complete.',
      'Cannot proceed with Sync Process. Tax Return has already been filed for selected Tax Year.',
      {
        p_consol_group_key: this.filingGroupSection,
        consol_group_key: this.filingGroupSection,
        FILER_LE_KEY: this.filerEntitySection,
        FEDERAL_STATE_FG: 'F',
      }
    );
  }

  syncPostSec250Process(): void {
    if (!this.validateSec250()) return;
    if (parseInt(this.selectedTaxYear, 10) < 2020) {
      this.gtwSnackbarService.openSnackBar(
        'Post-Sec.250 Sync is available only from tax year 2020!',
        'gtw-snackbar--red',
        true,
        5
      );
      return;
    }
    this.runSyncWithLockCheck(
      'Are you sure to initiate the Sync process for Post-Section 250?',
      'Sync process for Post-Section 250',
      'ux0j8a',
      'Sync Process for Post-Section 250 is initiated. Email will be sent, after the process is complete.',
      'Cannot proceed with Sync Process. Tax Return has already been filed for selected Tax Year.',
      {
        p_consol_group_key: this.filingGroupSection,
        consol_group_key: this.filingGroupSection,
        FILER_LE_KEY: this.filerEntitySection,
        FEDERAL_STATE_FG: 'F',
      }
    );
  }

  private validateSec250(): boolean {
    if (!this.validateTaxYearAndScenario()) return false;
    if (!this.filingGroupSection) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Filing Group.',
        'gtw-snackbar--red',
        true,
        5
      );
      return false;
    }
    if (!this.filerEntitySection) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Filing Entity.',
        'gtw-snackbar--red',
        true,
        5
      );
      return false;
    }
    return true;
  }

  autoTagProcess(): void {
    if (!this.validateTaxYearAndScenario()) return;
    if (!this.parentME || this.parentME.length == 0) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the valid ME(s).',
        'gtw-snackbar--red',
        true,
        5
      );
      return;
    }
    if (!this.filingGroup) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Filing Group.',
        'gtw-snackbar--red',
        true,
        5
      );
      return;
    }
    if (!this.filerEntity || this.filerEntity.length == 0) {
      this.gtwSnackbarService.openSnackBar(
        'Please select a valid Filing Entity.',
        'gtw-snackbar--red',
        true,
        5
      );
      return;
    }

    this.confirmAction(
      'Initiate Auto Tag Creation Process',
      'Are you sure to initiate the Auto Tag Creation?',
      () => this.executeAutoTag()
    );
  }

  private executeAutoTag(): void {
    const checkLockedbj: any[] = [];
    for (const me of this.parentME) {
      checkLockedbj.push({
        me_code: me.PARENT_ME_CODE,
        consol_group_key: this.filingGroup,
        combination_key: this.filerEntity
          .map((f: any) => f.FILER_COMBINATION_KEY)
          .join(),
      });
    }

    const issueParams = this.gtwGlobal.isIssueIDEnabled(
      this.selectedTaxYear,
      this.selectedScenario
    );

    const params: any = {
      ...issueParams,
      sso_id: this.userSettings?.user?.sso_id,
      action_code: 'zfwjr9',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      consol_group_key: this.filingGroup,
      FILER_LE_KEY: this.filerEntity
        .map((f: any) => f.FILER_LE_KEY)
        .join(),
      me_key: this.parentME
        .map((m: any) => m.PARENT_ME_KEY)
        .join(),
      issue_id: this.globalParams.issue_id,
      issue_short_desc: this.globalParams.issue_short_desc,
      checkLockedbj: JSON.stringify(checkLockedbj),
    };

    this.beginRequest();
    this.syncProcessService
      .saveAutoTag(
        this.GENERAL_CONFIG.base_url + '/saveJsonObject',
        params
      ).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.endRequest()))
      .subscribe(
        (result: any) => {
          if (result?.callSuccess != 1 && result?.errorMessage) {
            this.gtwSnackbarService.openSnackBar(
              result.errorMessage,
              'gtw-snackbar--red',
              true,
              4
            );
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Process to Create a Tag is initiated. Email will be sent, after the process is complete.',
              'gtw-snackbar--green',
              true,
              4
            );
          }
          this.cdr.detectChanges();
        },
        () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred. Please try again.',
            'gtw-snackbar--red',
            true,
            4
          );
          this.cdr.detectChanges();
        }
      );
  }

  compareMultiSelectItems(a: any, b: any): boolean {
    return a && b && a.PARENT_ME_KEY == b.PARENT_ME_KEY;
  }

  compareFilerEntityItems(a: any, b: any): boolean {
    return a && b && a.FILER_LE_KEY == b.FILER_LE_KEY;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onDropdownSelectItem(_event: any): void {}

  private refreshTaxYearDropdown(): void {
    this.filteredTaxYearDataView = mapToAppkitDropdownItems(this.filteredTaxYearData, 'CODE_DISP_DESC', 'CODE_DISP_DESC');
    this.selectedTaxYearItem = syncAppkitDropdownSelection(
      this.filteredTaxYearDataView,
      this.selectedTaxYear,
      'CODE_DISP_DESC',
      'CODE_DISP_DESC'
    );
  }

  private refreshScenarioDropdown(): void {
    this.scenarioDropDownView = mapToAppkitDropdownItems(this.scenarioDropDown, 'LABEL', 'VALUE');
    this.selectedScenarioItem = syncAppkitDropdownSelection(
      this.scenarioDropDownView,
      this.selectedScenario,
      'LABEL',
      'VALUE'
    );
  }

  private refreshFilingGroupSectionDropdown(): void {
    this.filingGroupDropWoPshipView = mapToAppkitDropdownItems(this.filingGroupDropWoPship, 'LABEL', 'VALUE');
    this.filingGroupSectionItem = syncAppkitDropdownSelection(
      this.filingGroupDropWoPshipView,
      this.filingGroupSection,
      'LABEL',
      'VALUE'
    );
  }

  private refreshFilerEntitySectionDropdown(): void {
    this.filingEnityDetailsForSectionDetailsView = mapToAppkitDropdownItems(
      this.filingEnityDetailsForSectionDetails,
      'FILER_LE',
      'FILER_LE_KEY'
    );
    this.filerEntitySectionItem = syncAppkitDropdownSelection(
      this.filingEnityDetailsForSectionDetailsView,
      this.filerEntitySection,
      'FILER_LE',
      'FILER_LE_KEY'
    );
  }

  private refreshFilingGroupDropdown(): void {
    this.filingGroupDropDownView = mapToAppkitDropdownItems(this.filingGroupDropDown, 'LABEL', 'VALUE');
    this.filingGroupItem = syncAppkitDropdownSelection(
      this.filingGroupDropDownView,
      this.filingGroup,
      'LABEL',
      'VALUE'
    );
  }

  onSelectedTaxYearSelect(event: any): void {
    handleAppkitValueSelect(event, this, 'selectedTaxYearItem', 'selectedTaxYear', () => this.getScenarioDropDown());
  }

  onSelectedScenarioSelect(event: any): void {
    handleAppkitValueSelect(event, this, 'selectedScenarioItem', 'selectedScenario', () => this.onScenarioChange());
  }

  onFilingGroupSectionSelect(event: any): void {
    handleAppkitValueSelect(event, this, 'filingGroupSectionItem', 'filingGroupSection', () => this.getFilingEnityDetailsForSection());
  }

  onFilerEntitySectionSelect(event: any): void {
    handleAppkitValueSelect(event, this, 'filerEntitySectionItem', 'filerEntitySection');
  }

  onFilingGroupSelect(event: any): void {
    handleAppkitValueSelect(event, this, 'filingGroupItem', 'filingGroup', () => this.getFilingEnityDetails());
  }
  
  onDropdownFilterResult(key: string, event: any): void {
    this.dropdownLists[key] = event?.result || [];
  }

  private toAppkitOptions(items: any[]): any[] {
    return (items || []).map((item: any) => {
      item.showCheckbox = true;
      item.role = 'checkbox';
      return item;
    });
  }

  private syncDropdownList(key: string, master: any[]): void {
    this.dropdownLists[key] = this.toAppkitOptions(master).slice();
  }
}
